import { isServer } from 'lit';
import { getElementNormalizedAttribute } from '../get-element-normalized-attribute/get-element-normalized-attribute.js';
import { getRandomNumber } from '../get-random-number/get-random-number.js';
import { LinkedSlottedElementsEvent } from './linked-slotted-elements-event.js';
import debounce from '../../external/lodash-es@4.17.21/external/lodash-es/debounce.js';

class LinkSlottedFormElementsController {
  constructor({ enabled, host, selectors }) {
    this._alteredIds = {
      helps: [],
      inputs: [],
      inputsAriaDescribedBy: [],
      labels: [],
      validations: []
    };
    this._originalIds = {
      helps: [],
      inputs: [],
      inputsAriaDescribedBy: [],
      labels: [],
      validations: []
    };
    this._selectors = {
      help: void 0,
      input: void 0,
      label: void 0,
      validation: void 0
    };
    this.host = host;
    this.hostElement = host;
    this._selectors = selectors;
    this.enabled = enabled ?? true;
    this.host.addController(this);
    this.handleSlotChange = debounce(this.handleSlotChange.bind(this), 0);
    this.willUpdate = debounce(this.willUpdate.bind(this), 0);
    this.deepSlotChangeCallback = debounce(this.deepSlotChangeCallback.bind(this), 0);
  }
  get _crntInputs() {
    if (!isServer && this._selectors.crntInput) {
      return Array.from(this.hostElement.querySelectorAll(this._selectors.crntInput));
    }
    return null;
  }
  get _helps() {
    if (!isServer && this._selectors.help) {
      return Array.from(this.hostElement.querySelectorAll(this._selectors.help));
    }
    return null;
  }
  get _inputs() {
    if (!isServer && this._selectors.input) {
      return Array.from(this.hostElement.querySelectorAll(this._selectors.input));
    }
    return null;
  }
  get _labels() {
    if (!isServer && this._selectors.label) {
      return Array.from(this.hostElement.querySelectorAll(this._selectors.label));
    }
    return null;
  }
  get _validations() {
    if (!isServer && this._selectors.validation) {
      return Array.from(this.hostElement.querySelectorAll(this._selectors.validation));
    }
    return null;
  }
  deepSlotChangeCallback(mutations) {
    mutations.filter((mutation) => this._inputs?.some(({ tagName }) => tagName === mutation.target.nodeName)).forEach(({ target }) => {
      this.linkSlottedElements({ changedInput: target });
    });
    const elementsWithHiddenAttribute = [
      ...this._validations ?? [],
      ...this._labels ?? [],
      ...this._helps ?? [],
      ...this._crntInputs ?? []
    ];
    mutations.filter((mutation) => {
      const hasValidHiddenAttributeMutations = elementsWithHiddenAttribute.some(
        ({ tagName }) => tagName === mutation.target.nodeName
      );
      return hasValidHiddenAttributeMutations && mutation.attributeName === "hidden";
    }).forEach(() => {
      this.linkSlottedElements();
    });
  }
  handleSlotChange() {
    this.linkSlottedElements();
  }
  hostConnected() {
    if (!isServer) {
      this.setOriginalIds();
      this.linkSlottedElements();
    }
  }
  /**
   * Automatically link slotted elements to enable accessibility out-of-the-box
   * 1. Provide ids if not present
   * 2. Link inputs with their respective indexed labels via label's for attribute
   * 3. Link validation messages with inputs via aria-describedby, if present.
   * 4. Link help texts with inputs via aria-describedby, if present.
   * 5. Set state=invalid on slotted `crnt-input` elements if validation-messages are present.
   */
  linkSlottedElements({ changedInput } = {}) {
    if (!this.enabled) {
      return;
    }
    if (!!changedInput && this._alteredIds.inputs.includes(changedInput.id)) {
      return;
    }
    const inputsDescribedByIds = [.../* @__PURE__ */ new Set([...this._getAndSetValidationsIds(), ...this._getAndSetHelpsIds()])];
    this._setCrntInputState();
    this._getAndSetLabelsIds();
    this._setAndLinkInputs({ changedInput, inputsDescribedByIds });
    this.hostElement.dispatchEvent(new LinkedSlottedElementsEvent());
  }
  setAlteredIds() {
    this._alteredIds = {
      helps: this._attributeMapper(this._helps, "id"),
      inputs: this._attributeMapper(this._inputs, "id"),
      inputsAriaDescribedBy: this._attributeMapper(this._inputs, "aria-describedby"),
      labels: this._attributeMapper(this._labels, "id"),
      validations: this._attributeMapper(this._validations, "id")
    };
  }
  setOriginalIds() {
    this._originalIds = {
      helps: this._attributeMapper(this._helps, "id"),
      inputs: this._attributeMapper(this._inputs, "id"),
      inputsAriaDescribedBy: this._attributeMapper(this._inputs, "aria-describedby"),
      labels: this._attributeMapper(this._labels, "id"),
      validations: this._attributeMapper(this._validations, "id")
    };
  }
  willUpdate() {
    this.linkSlottedElements();
  }
  _attributeMapper(elements, attributeKey) {
    if (!elements) {
      return [];
    }
    return elements.map((el) => getElementNormalizedAttribute(el, attributeKey));
  }
  _getAndSetHelpsIds() {
    const helpIds = [];
    if (!this._helps) {
      this._alteredIds.helps = [];
      return helpIds;
    }
    this._helps?.forEach((help, index) => {
      const helpId = this._alteredIds.helps[index] ?? this._originalIds.helps[index] ?? `${this._getRandomValue()}-help`;
      help.id = helpId;
      this._alteredIds.helps[index] = helpId;
      if (this._isVisibleElement(help)) {
        helpIds.push(helpId);
      }
    });
    return helpIds;
  }
  _getAndSetLabelsIds() {
    if (!this._labels) {
      this._alteredIds.labels = [];
      return [];
    }
    return this._labels?.map((label, index) => {
      const labelId = this._alteredIds.labels[index] ?? this._originalIds.labels[index] ?? `${this._getRandomValue()}-label`;
      label.id = labelId;
      this._alteredIds.labels[index] = labelId;
      return labelId;
    });
  }
  _getAndSetValidationsIds() {
    const validationIds = [];
    if (!this._validations) {
      this._alteredIds.validations = [];
      return validationIds;
    }
    this._validations?.forEach((validation, index) => {
      const validationId = this._alteredIds.validations[index] ?? this._originalIds.validations[index] ?? `${this._getRandomValue()}-validation`;
      validation.id = validationId;
      this._alteredIds.validations[index] = validationId;
      if (this._isVisibleElement(validation)) {
        validationIds.push(validationId);
      }
    });
    return validationIds;
  }
  _getInputId({ changedInput, index }) {
    const changedInputIndex = changedInput ? this._inputs?.findIndex((inp) => inp.isEqualNode(changedInput)) : void 0;
    let id = this._alteredIds.inputs[index] ?? this._originalIds.inputs[index] ?? `${this._getRandomValue()}-input`;
    if (changedInput && changedInputIndex === index) {
      id = changedInput.id;
    }
    return id;
  }
  _getRandomValue() {
    return getRandomNumber();
  }
  _getVisibleElements(elements) {
    return elements.filter(this._isVisibleElement.bind(this));
  }
  _isVisibleElement(element) {
    return !element.hasAttribute("hidden");
  }
  _setAndLinkInputs({
    changedInput,
    inputsDescribedByIds
  }) {
    this._inputs?.forEach((input, index) => {
      const inputId = this._getInputId({ changedInput, index });
      input.ariaInvalid = this._getVisibleElements(this._validations ?? []).length ? "true" : null;
      input.id = inputId;
      const localAriaDescribedBy = this._originalIds.inputsAriaDescribedBy[index]?.split(" ") ?? [];
      const allInputDescribedByIds = [.../* @__PURE__ */ new Set([...inputsDescribedByIds, ...localAriaDescribedBy])].filter(
        (id) => !!id
      );
      if (allInputDescribedByIds.length) {
        input.setAttribute("aria-describedby", allInputDescribedByIds.join(" "));
      } else {
        input.removeAttribute("aria-describedby");
      }
      const label = this._labels?.[index];
      if (label) {
        label.htmlFor = inputId;
      }
      this._alteredIds.inputs[index] = inputId;
    });
  }
  _setCrntInputState() {
    this._crntInputs?.forEach((crntInput) => {
      crntInput.state = this._getVisibleElements(this._validations ?? []).length ? "invalid" : void 0;
    });
  }
}

export { LinkSlottedFormElementsController };
